import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Pause, Play } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { usePlayer } from "@/context/PlayerContext";
import { Reveal } from "@/components/Reveal";
import { CountdownBanner } from "@/components/CountdownBanner";
import { Marquee } from "@/components/Marquee";
import { ShareTrackButton } from "@/components/ShareTrackButton";
import { SocialIcons } from "@/components/SocialIcons";

const MaskedLine = ({ children, delay }) => (
  <span className="line-mask">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ delay, duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const fmtDate = (d) => {
  if (!d) return "";
  if (/^\d{4}$/.test(d)) return d;
  try {
    return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return d;
  }
};

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [news, setNews] = useState([]);
  const { playTrack, openDetails, track, playing } = usePlayer();
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 900], [0, 220]);
  const bgOpacity = useTransform(scrollY, [0, 700], [1, 0.25]);

  useEffect(() => {
    api.get("/releases").then((r) => setFeatured(r.data.filter((t) => t.featured).slice(0, 8))).catch(() => {});
    api.get("/news").then((r) => setNews(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden" data-testid="hero">
        <motion.div className="absolute inset-0" style={{ y: bgY, opacity: bgOpacity }}>
          <img
            src="/assets/hero-sunset.webp"
            alt="Golden sunset on the horizon over still water under a starlit sky"
            className="w-full h-full object-cover scale-110"
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-b from-navy-950/60 via-navy-950/40 to-navy-950" />
        <div className="noise-overlay" />
        <div className="relative z-10 text-center px-5 pt-20 pb-8">
          <motion.img
            src="/assets/logo-lockup.png"
            alt="Open Horizon Records"
            className="mx-auto w-full max-w-xl sm:max-w-3xl [@media(max-height:850px)]:sm:max-w-2xl animate-floaty drop-shadow-[0_0_60px_rgba(212,175,55,0.45)]"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
            data-testid="hero-logo"
          />
          <h1 className="font-serif text-4xl sm:text-6xl lg:text-7xl [@media(max-height:850px)]:lg:text-6xl text-slate-50 tracking-tight leading-[1.05] mt-6">
            <MaskedLine delay={0.35}>Music Beyond</MaskedLine>
            <MaskedLine delay={0.5}>
              <span className="text-gold-gradient">The Horizon</span>
            </MaskedLine>
          </h1>
          <motion.p
            className="mt-4 max-w-xl mx-auto text-slate-300/90 font-light leading-relaxed"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 1 }}
          >
            Cinematic, story-driven music — and bespoke songs written around the moments
            that matter most.
          </motion.p>
          <motion.div
            className="mt-7 flex flex-wrap items-center justify-center gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.8 }}
          >
            <Link to="/music" className="btn-gold" data-testid="explore-music-cta">
              Explore Our Music <ArrowRight size={16} />
            </Link>
            <Link to="/bespoke" className="btn-ghost-gold" data-testid="bespoke-cta">
              Commission Your Song
            </Link>
          </motion.div>
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5, duration: 1 }}
            onClick={() => document.querySelector('[data-testid="countdown-banner"], [data-testid="featured-releases"]')?.scrollIntoView({ behavior: "smooth" })}
            className="mt-7 mx-auto flex flex-col items-center gap-2 text-gold-300/70 hover:text-gold-200 transition-colors cursor-pointer"
            data-testid="hero-scroll-cue"
            aria-label="Scroll down to the music"
          >
            <span className="text-[10px] tracking-[0.35em] uppercase">Scroll</span>
            <motion.span
              className="block w-px h-8 bg-gradient-to-b from-gold-400/80 to-transparent"
              animate={{ opacity: [1, 0.3, 1] }}
              transition={{ repeat: Infinity, duration: 2.4 }}
            />
          </motion.button>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3, duration: 1 }}
            className="mt-7 flex justify-center"
          >
            <SocialIcons size={20} />
          </motion.div>
        </div>
      </section>

      <Marquee
        items={[
          "Original Story-Driven Songs",
          "Bespoke Music For Every Occasion",
          "Weddings",
          "Anniversaries",
          "Memorials",
          "Tributes",
          "Music Beyond The Horizon",
        ]}
      />

      <CountdownBanner />

      {/* FEATURED RELEASES */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28" data-testid="featured-releases">
        <Reveal>
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="eyebrow mb-3">Latest Releases</p>
              <h2 className="font-serif text-3xl sm:text-5xl text-slate-50">Featured Music</h2>
            </div>
            <Link
              to="/music"
              className="hidden sm:inline-flex items-center gap-2 text-sm text-gold-300 hover:text-gold-200 transition-colors"
              data-testid="view-all-music-link"
            >
              All music <ArrowRight size={15} />
            </Link>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {featured.map((t, i) => {
            const active = track?.id === t.id;
            return (
              <Reveal key={t.id} delay={i * 0.12}>
                <div
                  className="group relative rounded-2xl overflow-hidden border border-white/5 bg-navy-900/60 hover:border-gold-400/40 transition-colors duration-500"
                  data-testid={`featured-card-${t.id}`}
                >
                  <div className="relative aspect-square overflow-hidden">
                    <img
                      src={mediaUrl(t.cover_url)}
                      alt={`${t.title} cover art`}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy-950/90 via-transparent to-transparent" />
                    <div className="absolute top-3 right-3 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-300">
                      <ShareTrackButton track={t} compact testid={`featured-share-${t.id}`} className="bg-navy-950/70 backdrop-blur" />
                    </div>
                    <button
                      onClick={() => playTrack(t)}
                      data-testid={`track-play-button-${t.id}`}
                      aria-label={`Play ${t.title} preview`}
                      className="absolute bottom-4 right-4 w-12 h-12 rounded-full flex items-center justify-center text-navy-950 opacity-0 group-hover:opacity-100 transition-all duration-300 hover:scale-110"
                      style={{ background: "linear-gradient(135deg,#F59E0B,#D4AF37 50%,#B38728)" }}
                    >
                      {active && playing ? <Pause size={18} fill="currentColor" /> : <Play size={18} fill="currentColor" className="ml-0.5" />}
                    </button>
                  </div>
                  <div className="p-5 flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-serif text-lg text-slate-100">{t.title}</h3>
                      <p className="text-xs text-slate-500 mt-1">{fmtDate(t.release_date)}</p>
                    </div>
                    <button
                      onClick={() => openDetails(t)}
                      data-testid={`track-details-${t.id}`}
                      className="text-[11px] tracking-[0.2em] uppercase text-gold-400/80 hover:text-gold-300 transition-colors mt-1"
                    >
                      Details
                    </button>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* BESPOKE TEASER */}
      <section className="relative overflow-hidden" data-testid="bespoke-teaser">
        <div className="absolute inset-0">
          <img src="/assets/header-dark.webp" alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-950 via-navy-950/85 to-navy-950/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <p className="eyebrow mb-3">Bespoke Music</p>
            <h2 className="font-serif text-3xl sm:text-5xl text-slate-50 leading-tight">
              Your Story. Your Song. <span className="text-gold-gradient">Created With You.</span>
            </h2>
            <p className="mt-6 text-slate-300/85 font-light leading-relaxed max-w-lg">
              Every occasion. Every emotion. Every story deserves its own song. For weddings,
              anniversaries, birthdays, celebrations, memorials and tributes — a complete,
              original, professionally produced song with lyrics written around your story.
            </p>
            <p className="mt-4 text-sm text-gold-200/80">Packages from £99 — payment via PayPal or bank transfer.</p>
            <Link to="/bespoke" className="btn-gold mt-8" data-testid="teaser-bespoke-cta">
              Start Your Enquiry <ArrowRight size={16} />
            </Link>
          </Reveal>
          <Reveal delay={0.15} className="hidden lg:block">
            <img
              src="/assets/about-logo.webp"
              alt="Open Horizon Records artwork"
              className="rounded-2xl border border-gold-400/20 shadow-[0_20px_80px_rgba(0,0,0,0.6)] w-full h-auto max-w-md ml-auto"
            />
          </Reveal>
        </div>
      </section>

      {/* LATEST NEWS */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28" data-testid="latest-news">
        <Reveal>
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="eyebrow mb-3">Latest From The Horizon</p>
              <h2 className="font-serif text-3xl sm:text-5xl text-slate-50">News & Stories</h2>
            </div>
            <Link
              to="/news"
              className="hidden sm:inline-flex items-center gap-2 text-sm text-gold-300 hover:text-gold-200 transition-colors"
              data-testid="view-all-news-link"
            >
              All news <ArrowRight size={15} />
            </Link>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {news.map((n, i) => (
            <Reveal key={n.id} delay={i * 0.12}>
              <Link
                to={`/news/${n.id}`}
                data-testid={`news-card-${n.id}`}
                className="group block rounded-2xl overflow-hidden border border-white/5 bg-navy-900/60 hover:border-gold-400/40 transition-colors duration-500"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img
                    src={mediaUrl(n.cover_url)}
                    alt={n.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <p className="text-[11px] tracking-[0.25em] uppercase text-gold-400/80">{fmtDate(n.date)}</p>
                  <h3 className="font-serif text-xl text-slate-100 mt-2 group-hover:text-gold-200 transition-colors">
                    {n.title}
                  </h3>
                  <p className="text-sm text-slate-400 mt-3 line-clamp-2 font-light">{n.body.split("\n")[0]}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
